import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sparkle, Rocket, Trophy, Buildings, PiggyBank, Vault, Money, ShareNetwork, Path } from '@phosphor-icons/react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';

const ICON_MAP = {
  rocket: Rocket,
  trophy: Trophy,
  buildings: Buildings,
  piggy: PiggyBank,
  vault: Vault,
  money: Money,
  share: ShareNetwork,
};

const CATEGORY_COLORS = {
  milestone: '#0A1128',
  savings: '#2D6A4F',
  net_worth: '#C5A059',
  retirement: '#0A1128',
  wealth_health: '#C5A059',
  income: '#B7791F',
};

const CATEGORY_LABELS = {
  milestone: 'Milestone',
  savings: 'Savings',
  net_worth: 'Net Worth',
  retirement: 'Retirement',
  wealth_health: 'Wealth Health',
  income: 'Income',
};

const FinancialJourneyTimeline = () => {
  useTrackToolUsage('journey_timeline');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/timeline').then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => {
      toast.error('Failed to load timeline');
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Loading your journey...</div>;
  }

  if (!data) return null;

  const filteredEvents = filter === 'all'
    ? data.events
    : data.events.filter(e => e.category === filter);

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Your Financial Journey:</strong> A chronological record of every milestone, snapshot, goal, and step along your wealth-building path. Reflect on how far you've come.
          </p>
        </div>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Financial Journey Timeline&trade;
          </h1>
          <p className="text-base text-[#4A5568]">{data.total_events} event{data.total_events !== 1 ? 's' : ''} recorded</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="timeline-filters">
        <button
          onClick={() => setFilter('all')}
          data-testid="filter-all"
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            filter === 'all' ? 'bg-[#0A1128] text-white' : 'bg-white text-[#4A5568] border border-slate-200 hover:border-[#C5A059]'
          }`}
        >
          All ({data.events.length})
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
          const count = data.events.filter(e => e.category === key).length;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`filter-${key}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                filter === key ? 'bg-[#0A1128] text-white' : 'bg-white text-[#4A5568] border border-slate-200 hover:border-[#C5A059]'
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {filteredEvents.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-12 text-center">
          <p className="text-[#4A5568]">No events in this category yet. Keep building!</p>
        </div>
      ) : (
        <div className="relative" data-testid="timeline-container">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#C5A059] via-slate-200 to-transparent" />

          <div className="space-y-6">
            {filteredEvents.map((event, idx) => {
              const Icon = ICON_MAP[event.icon] || Trophy;
              const color = CATEGORY_COLORS[event.category] || '#C5A059';
              const eventDate = event.date ? new Date(event.date) : null;

              return (
                <div key={`${event.type}-${idx}`} className="relative pl-16" data-testid={`timeline-event-${idx}`}>
                  {/* Icon */}
                  <div className="absolute left-0 top-0 w-12 h-12 rounded-full flex items-center justify-center shadow-md"
                    style={{ backgroundColor: color }}>
                    <Icon size={22} weight="duotone" className="text-white" />
                  </div>

                  {/* Card */}
                  <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4 dashboard-card">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-[#0A1128]">{event.title}</h3>
                        <p className="text-sm text-[#4A5568] mt-1">{event.description}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded text-xs font-medium uppercase tracking-[0.06em] whitespace-nowrap"
                        style={{ color: color, backgroundColor: `${color}15` }}>
                        {CATEGORY_LABELS[event.category] || event.category}
                      </span>
                    </div>
                    {eventDate && (
                      <p className="text-xs text-[#4A5568] mt-2">
                        {eventDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        <span className="mx-2 text-[#A0AABF]">·</span>
                        {eventDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialJourneyTimeline;
